// src/pages/TransactionsPage.js
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useDebounce } from "use-debounce";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Calendar,
  User,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  XCircle,
  XCircleIcon,
  RefreshCw,
  Download,
  Eye,
  Edit,
} from "lucide-react";

const PAGE_SIZE = 20;

const TransactionsPage = () => {
  const { projectName } = useParams();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Search & filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [assignedFilter, setAssignedFilter] = useState("all"); // all | yes | no

  // Sorting and pagination states
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);

  // Loading / error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reassign modal states
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTransactionId, setReassignTransactionId] = useState(null);
  const [newPlotNo, setNewPlotNo] = useState("");
  const [reassignComment, setReassignComment] = useState("");

  // Available plots
  const [availablePlots, setAvailablePlots] = useState([]);
  const [loadingPlots, setLoadingPlots] = useState(false);

  // Plot search states
  const [plotSearchTerm, setPlotSearchTerm] = useState("");
  const [debouncedPlotSearchTerm] = useDebounce(plotSearchTerm, 300);
  const [showPlotDropdown, setShowPlotDropdown] = useState(false);

  // Fetch transactions and plots
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadingPlots(true);

        const [txResponse, plotsResponse] = await Promise.all([
          axios.get(
            `https://workspace.optiven.co.ke/api/mib/projects/${encodeURIComponent(
              projectName
            )}/transactions`
          ),
          axios.get(
            `https://workspace.optiven.co.ke/api/mib/projects/${encodeURIComponent(
              projectName
            )}/plots`
          ),
        ]);

        if (Array.isArray(txResponse.data.transactions)) {
          const enhanced = txResponse.data.transactions.map((tx) => ({
            ...tx,
            assigned: tx.assigned_to && tx.assigned_to.trim() !== "",
          }));
          setTransactions(enhanced);
        } else {
          throw new Error("Unexpected transaction format");
        }

        if (Array.isArray(plotsResponse.data.plots)) {
          setAvailablePlots(plotsResponse.data.plots);
        }
      } catch (err) {
        console.error(err);
        setError(
          err.response?.data?.message || "Failed to load transactions or plots."
        );
        toast.error(
          err.response?.data?.message || "Failed to load transactions or plots."
        );
      } finally {
        setLoading(false);
        setLoadingPlots(false);
      }
    };
    fetchData();
  }, [projectName]);

  // Filter, search, sort
  useEffect(() => {
    let data = [...transactions];

    // Assigned filter
    if (assignedFilter !== "all") {
      const isAssigned = assignedFilter === "yes";
      data = data.filter((t) => t.assigned === isAssigned);
    }

    // Search filter
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      data = data.filter((t) => {
        return (
          t.id.toString().toLowerCase().includes(term) ||
          t.Receipt_No.toLowerCase().includes(term) ||
          (t.Narration || "").toLowerCase().includes(term)
        );
      });
    }

    // Sorting
    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal, bVal;
        switch (sortConfig.key) {
          case "id":
            aVal = a.id;
            bVal = b.id;
            break;
          case "Receipt_No":
            aVal = parseInt(a.Receipt_No.replace(/^REC/i, ""), 10) || 0;
            bVal = parseInt(b.Receipt_No.replace(/^REC/i, ""), 10) || 0;
            break;
          case "Amount_LCY":
            aVal = parseFloat(a.Amount_LCY) || 0;
            bVal = parseFloat(b.Amount_LCY) || 0;
            break;
          case "PAYMENT_DATE1":
            aVal = new Date(a.PAYMENT_DATE1).getTime() || 0;
            bVal = new Date(b.PAYMENT_DATE1).getTime() || 0;
            break;
          case "assigned":
            aVal = a.assigned ? 1 : 0;
            bVal = b.assigned ? 1 : 0;
            break;
          default:
            return 0;
        }
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredTransactions(data);
    setCurrentPage(1);
  }, [transactions, debouncedSearchTerm, assignedFilter, sortConfig]);

  // Paginated subset
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, currentPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / PAGE_SIZE)
  );

  // Filtered plots for search
  const filteredPlots = useMemo(() => {
    if (!debouncedPlotSearchTerm) {
      return availablePlots;
    }
    const searchTerm = debouncedPlotSearchTerm.toLowerCase();
    return availablePlots.filter((plot) =>
      plot.Plot_NO.toLowerCase().includes(searchTerm)
    );
  }, [availablePlots, debouncedPlotSearchTerm]);

  // Sorting handler
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Reassign logic
  const openReassignModal = (id) => {
    setReassignTransactionId(id);
    setShowReassignModal(true);
    setNewPlotNo("");
    setReassignComment("");
    setPlotSearchTerm("");
    setShowPlotDropdown(false);
  };
  const closeReassignModal = () => {
    setShowReassignModal(false);
    setReassignTransactionId(null);
    setNewPlotNo("");
    setReassignComment("");
    setPlotSearchTerm("");
    setShowPlotDropdown(false);
  };
  const handleReassign = async () => {
    if (!newPlotNo.trim() || !reassignComment.trim()) {
      toast.warn("Please fill out all reassignment fields.");
      return;
    }
    try {
      await axios.patch(
        `https://workspace.optiven.co.ke/api/mib/transactions/${reassignTransactionId}/assign`,
        {
          newPlotNo: newPlotNo.trim(),
          reassign_comment: reassignComment.trim(),
        }
      );
      // Update local state
      const updated = transactions.map((tx) =>
        tx.id === reassignTransactionId
          ? { ...tx, assigned_to: newPlotNo.trim(), assigned: true }
          : tx
      );
      setTransactions(updated);
      closeReassignModal();
      toast.success("Transaction reassigned successfully.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to reassign transaction.");
    }
  };

  // Plot selection handlers
  const handlePlotSelect = (plotNo) => {
    setNewPlotNo(plotNo);
    setPlotSearchTerm(plotNo);
    setShowPlotDropdown(false);
  };

  const handlePlotSearchChange = (e) => {
    const value = e.target.value;
    setPlotSearchTerm(value);
    setNewPlotNo(value);
    setShowPlotDropdown(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPlotDropdown && !event.target.closest(".plot-search-container")) {
        setShowPlotDropdown(false);
      }
    };

    if (showPlotDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPlotDropdown]);

  // Render
  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading transactions...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Transactions
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      {/* Toast container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Transactions for {projectName}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and review payment transactions
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {transactions.length}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Assigned</p>
                  <p className="text-2xl font-bold text-green-600">
                    {transactions.filter((t) => t.assigned).length}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Unassigned</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {transactions.filter((t) => !t.assigned).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters Section */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by ID, Receipt No, or Narration..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={assignedFilter}
                  onChange={(e) => setAssignedFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Transactions</option>
                  <option value="yes">Assigned Only</option>
                  <option value="no">Unassigned Only</option>
                </select>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      { label: "Transaction ID", key: "id", icon: CreditCard },
                      {
                        label: "Receipt No",
                        key: "Receipt_No",
                        icon: DollarSign,
                      },
                      { label: "Narration", key: null, icon: null },
                      { label: "Amount", key: "Amount_LCY", icon: DollarSign },
                      {
                        label: "Payment Date",
                        key: "PAYMENT_DATE1",
                        icon: Calendar,
                      },
                      { label: "Payment Mode", key: null, icon: CreditCard },
                      { label: "Status", key: "assigned", icon: CheckCircle },
                      { label: "Actions", key: null, icon: null },
                    ].map((col) => (
                      <th
                        key={col.label}
                        onClick={() => col.key && handleSort(col.key)}
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                          col.key
                            ? "cursor-pointer select-none hover:bg-gray-100"
                            : ""
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          {col.icon && <col.icon className="h-4 w-4" />}
                          <span>{col.label}</span>
                          {sortConfig.key === col.key && (
                            <span>
                              {sortConfig.direction === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <CreditCard className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              #{tx.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {tx.Receipt_No}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-gray-900 max-w-xs truncate"
                          title={tx.Narration}
                        >
                          {tx.Narration}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Ksh. {parseFloat(tx.Amount_LCY || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {tx.PAYMENT_DATE1
                            ? new Date(tx.PAYMENT_DATE1).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {tx.Pay_mode}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {tx.assigned ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Assigned
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openReassignModal(tx.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTransactions.length === 0 && (
                <div className="text-center py-12">
                  <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No transactions found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No transactions match your current search criteria.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * PAGE_SIZE + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        currentPage * PAGE_SIZE,
                        filteredTransactions.length
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {filteredTransactions.length}
                    </span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronDown className="h-5 w-5 transform rotate-90" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronDown className="h-5 w-5 transform -rotate-90" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeReassignModal}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <Edit className="h-6 w-6 text-yellow-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Reassign Payment
                      </h3>
                      <p className="text-sm text-gray-500">
                        Move transaction to a different plot
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeReassignModal}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                      <div>
                        <h4 className="text-sm font-medium text-red-800">
                          Important Notice
                        </h4>
                        <p className="text-sm text-red-700 mt-1">
                          Reassigning a payment to a different plot may affect
                          that plot's payment history. Please ensure this is the
                          correct action before proceeding.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select New Plot
                      </label>
                      {loadingPlots ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">
                            Loading plots...
                          </span>
                        </div>
                      ) : (
                        <div className="relative plot-search-container">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                              type="text"
                              placeholder="Search for a plot..."
                              value={plotSearchTerm}
                              onChange={handlePlotSearchChange}
                              onFocus={() => setShowPlotDropdown(true)}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {showPlotDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {filteredPlots.length > 0 ? (
                                filteredPlots.map((plot, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() =>
                                      handlePlotSelect(plot.Plot_NO)
                                    }
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                  >
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                        <span className="text-xs font-medium text-blue-600">
                                          {plot.Plot_NO.charAt(0)}
                                        </span>
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {plot.Plot_NO}
                                        </div>
                                        {plot.Plot_Name && (
                                          <div className="text-xs text-gray-500">
                                            {plot.Plot_Name}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                  No plots found matching "{plotSearchTerm}"
                                </div>
                              )}
                            </div>
                          )}

                          {newPlotNo && (
                            <div className="mt-2 flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              <span className="text-sm text-gray-700">
                                Selected:{" "}
                                <span className="font-medium">{newPlotNo}</span>
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reassignment Reason
                      </label>
                      <textarea
                        placeholder="Enter the reason for reassigning this payment..."
                        value={reassignComment}
                        onChange={(e) => setReassignComment(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                  <button
                    onClick={closeReassignModal}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReassign}
                    disabled={!newPlotNo.trim() || !reassignComment.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Reassignment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default TransactionsPage;
